import React from "react";
import { Row, Col, Modal, ModalBody, ModalHeader } from "reactstrap";
import { ServiceRequestDto, ServiceRequestLookupsDto } from "../../services/dto";
import {
    IDrawerStyles,
    useDrawerStyles,
    VfxLib,
    LoadingIndicator
} from "../../assets/vfx";
import Drawer from "../../assets/vfx/drawer/drawer";
import { DrawerActionButtonProps } from "../../assets/vfx/drawer/components/template/drawerTemplateButton";
import DisplayData from "../common/DisplayData/DisplayData";
import { ServiceRequestService } from "./serviceRequestService";
import { Priority, RequestStatus } from "./serviceRequestContainer";
import useServiceRequestStyles from "./styles";
import { FileHelper, FormatHelper } from "../../assets/utils/helpers";

interface ServiceRequestViewProps {
    show: boolean;
    data: ServiceRequestDto | undefined;
    lookups?: ServiceRequestLookupsDto | undefined;
    onHide: () => void;
    onAddNote: () => void;
    onComplete: () => void;
    onClose: () => void;
    isLoading?: boolean;
}

export const ServiceRequestView: React.FC<ServiceRequestViewProps> = ({
    show,
    data,
    lookups,
    onHide,
    onAddNote,
    onComplete,
    onClose,
    isLoading
}) => {
    const {
        data: notes,
        isLoading: isLoadingNotes,
    } = ServiceRequestService.getServiceRequestNotes(data?.id);

    const {
        data: actions,
        isLoading: isLoadingactions,
    } = ServiceRequestService.getServiceRequestActions(data?.id);

    const requestStyles = useServiceRequestStyles();
    const styles: IDrawerStyles = useDrawerStyles();
    const [showMap, setShowMap] = React.useState(false);

    if (isLoading) return (
        <Drawer.Styled
            show={show}
            onHide={onHide}
            title="LOADING..."
            headerbuttons={[VfxLib.drawerActionButtons({ drawerStyles: styles, onClick: onHide }).close]}
            placement="end"
            className="w-40"
        >
            <LoadingIndicator />
        </Drawer.Styled>
    );

    if (!data) return null;

    const priorityDisplay = lookups?.priority?.find(x => x.id === data.priorityId)?.display;

    const PriorityBadge = ({ pId }: { pId: string }) => {
        let style = requestStyles.priorityLow;
        if (pId === Priority.High) {
            style = requestStyles.priorityHigh;
        }
        else if (pId === Priority.Medium) {
            style = requestStyles.priorityMedium;
        }

        return (
            <div style={requestStyles.detailMarkerValue}>
                <span style={{ ...requestStyles.prioritySquare, ...style }} />
                <span>{priorityDisplay}</span>
            </div>
        );
    };

    const StatusBadge = ({ statusId }: { statusId: string }) => {
        let dotStyle = requestStyles.dotClosed;
        if (statusId === RequestStatus.Open) dotStyle = requestStyles.dotOpen;
        else if (statusId === RequestStatus.InProgress) dotStyle = requestStyles.dotProgress;

        return (
            <div style={requestStyles.detailMarkerValue}>
                <div style={{ ...requestStyles.statusSquare, ...dotStyle }} />
                <span>{lookups?.status?.find(x => x.id === statusId)?.display}</span>
            </div>
        );
    };

    const headerButtons = [
        VfxLib.drawerActionButtons({ drawerStyles: styles, onClick: onHide }).close
    ];

    const footerButtons: DrawerActionButtonProps[] = [
        {
            ...VfxLib.drawerActionButtons({ drawerStyles: styles }).saveButton,
            text: "Add Note",
            image: "bi bi-journal-plus",
            button_class: "btn-success",
            onClick: onAddNote,
        },
        {
            ...VfxLib.drawerActionButtons({ drawerStyles: styles }).saveButton,
            text: "Complete",
            image: "bi bi-check2-circle",
            button_class: "btn-success",
            onClick: onComplete,
        },
        {
            ...VfxLib.drawerActionButtons({ drawerStyles: styles }).cancel,
            text: "Close Request",
            image: "bi bi-x-circle",
            onClick: onClose,
        },
    ];

    const hasCoordinates =
        typeof data.latitude === "number" &&
        Number.isFinite(data.latitude) &&
        typeof data.longitude === "number" &&
        Number.isFinite(data.longitude);
    const mapUrl = hasCoordinates
        ? `https://maps.google.com/maps?q=${data.latitude},${data.longitude}&z=16&output=embed`
        : "";
    const stateDisplay = lookups?.state?.find(x => x.id === data.stateId)?.display || "";
    const cityState = [data.city, stateDisplay].filter(Boolean).join(", ");
    const cityStateZip = [cityState, data.zip].filter(Boolean).join(" ");
    const addressLines = [data.addressLine1, data.addressLine2, cityStateZip].filter(Boolean);
    const imageExtensions = ["jpg", "jpeg", "png", "webp", "gif"];
    const images = data.attachments?.filter(a => imageExtensions.includes((a.extension ?? "").replace(".", "").toLowerCase())) ?? [];
    const otherAttachments = data.attachments?.filter(a => !imageExtensions.includes((a.extension ?? "").replace(".", "").toLowerCase())) ?? [];

    const SectionHeader = ({ title }: { title: string }) => (
        <div style={requestStyles.sectionHeader}>{title}</div>
    );

    return (
        <Drawer.Styled
            show={show}
            onHide={onHide}
            title="Request Details"
            headerbuttons={headerButtons}
            footerbuttons={footerButtons}
            placement="end"
            className="w-40"
        >
            <div>
                <Row className="gy-4">
                    <Col xs={12}>
                        <DisplayData text="ID" importedData={data.requestNumber} />
                        <DisplayData text="Type" importedData={lookups?.requestType?.find(x => x.id === data.requestTypeId)?.display} />
                        <DisplayData text="Priority" importedData={<PriorityBadge pId={data.priorityId} />} />
                        <DisplayData text="Status" importedData={<StatusBadge statusId={data.statusId ?? ""} />} />
                        <div style={requestStyles.locationWithMap}>
                            <div style={requestStyles.locationDetails}>
                              
                                    <DisplayData
                                        text="Location"
                                        importedData={
                                            <>
                                                {addressLines.map((line, index) => (
                                                    <React.Fragment key={index}>
                                                        {line}
                                                        {index < addressLines.length - 1 && <br />}
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        }
                                    />
                            
                            </div>
                            {hasCoordinates && (
                                <button
                                    type="button"
                                    aria-label="View location map"
                                    title="View location map"
                                    style={requestStyles.mapButton}
                                    onClick={() => setShowMap(true)}
                                >
                                    <i className="bi bi-geo-alt-fill" />
                                </button>
                            )}
                        </div>
                        {data.submitDate && (
 
                            <DisplayData text="Submitted" importedData={FormatHelper.formatDate(data.submitDate)} />
                        )}
                        {data.description && (
                            <DisplayData
                                text="Description"
                                importedData={data.description}
                            />
                        )}
                    </Col>

                    {(images.length > 0 || otherAttachments.length > 0) && (
                        <Col xs={12}>
                            <SectionHeader title="Attachments" />
                            {images.length > 0 && (
                                <div style={requestStyles.attachmentContainer}>
                                    {images.map((attachment, idx) => (
                                        <img
                                            key={idx}
                                            src={FileHelper.FileToBase64String({ image: attachment })}
                                            alt={attachment.fileName}
                                            style={requestStyles.attachmentImage as React.CSSProperties}
                                        />
                                    ))}
                                </div>
                            )}
                            {otherAttachments.length > 0 && (
                                <div style={requestStyles.attachmentList}>
                                    {otherAttachments.map((attachment, idx) => (
                                        <a
                                            key={idx}
                                            href="#"
                                            style={requestStyles.attachmentLink as React.CSSProperties}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                FileHelper.downloadDocument(
                                                    attachment.content as unknown as string,
                                                    attachment.mimeType || "",
                                                    attachment.fileName || "",
                                                );
                                            }}
                                        >
                                            <i className="bi bi-file-earmark-arrow-down" />
                                            {attachment.fileName || "Download"}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </Col>
                    )}

                    {!isLoadingNotes && notes && notes.length > 0 && (
                        <Col xs={12}>
                            <SectionHeader title="Notes" />
                            <div className="mt-2">
                                {notes.slice().reverse().map((note, idx) => (
                                    <div key={idx} style={requestStyles.historyItem}>
                                        <div style={requestStyles.historyHeader}>
                                            <span>{note.commentBy}</span>
                                            <span>{FormatHelper.formatDateTime(note.commentDate)}</span>
                                        </div>
                                        <div style={requestStyles.historyContent}>{note.comment}</div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    )}

                    {!isLoadingactions && actions && actions.length > 0 && (
                        <Col xs={12}>
                            <SectionHeader title="Activity Log" />
                            <div className="mt-2">
                                {actions.slice().reverse().map((act, idx) => (
                                    <div key={idx} style={{ ...requestStyles.historyItem, ...requestStyles.actionItem }}>
                                        <div style={requestStyles.historyHeader}>
                                            <span>{act.createdBy}</span>
                                            <span>{FormatHelper.formatDateTime(act.actionDate)}</span>
                                        </div>
                                        <div style={requestStyles.historyContent}>{act.actionType}</div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    )}
                </Row>
            </div>
            <Modal isOpen={showMap} toggle={() => setShowMap(false)} size="lg" centered>
                <ModalHeader toggle={() => setShowMap(false)}>Location Map</ModalHeader>
                <ModalBody>
                    {hasCoordinates && (
                        <iframe
                            title="Service request location map"
                            src={mapUrl}
                            style={requestStyles.mapFrame}
                            loading="lazy"
                        />
                    )}
                </ModalBody>
            </Modal>
        </Drawer.Styled>
    );
};

